/**
 * Parse a YYYY-MM-DD date string into a Date at local midnight.
 * Avoids the UTC-midnight interpretation that `new Date("YYYY-MM-DD")` uses,
 * which causes off-by-one-day errors when displayed in timezones behind UTC.
 */
export function parseDateString(
  dateStr: string | null | undefined
): Date | null {
  if (!dateStr) return null;
  const str = String(dateStr).trim();
  // Handle ISO datetime strings — extract just the date portion
  const datePart = str.includes("T") ? str.split("T")[0] : str;

  let year;
  let month;
  let day;

  if (datePart.includes("-") && datePart.split("-")[0].length === 4) {
    const parts = datePart.split("-");
    if (parts.length !== 3) return null;
    [year, month, day] = parts.map(Number);
  } else if (datePart.includes("/") || datePart.includes("-")) {
    const delimiter = datePart.includes("/") ? "/" : "-";
    const parts = datePart.split(delimiter);
    if (parts.length !== 3) return null;
    [month, day, year] = parts.map(Number);
    if (year < 100) year += 2000;
  } else {
    return null;
  }

  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  // Use noon (12:00:00) instead of midnight to stay clear of DST/timezone
  // boundaries — JSON.stringify on a Date calls toISOString() (UTC), so a
  // midnight local Date near a DST boundary can shift to the previous day when
  // round-tripped through JSON (e.g. autosave comparison). Noon is always safe.
  return new Date(year, month - 1, day, 12, 0, 0);
}

/**
 * Format a Date object as YYYY-MM-DD using local date components.
 * This avoids timezone-shifted ISO strings from `toISOString()`.
 */
export function formatDateForApi(
  date: Date | string | null | undefined
): string | null {
  if (!date) return null;
  const resolved = typeof date === "string" ? parseDateString(date) : date;
  if (!resolved || isNaN(resolved.getTime())) return null;
  const y = resolved.getFullYear();
  const m = String(resolved.getMonth() + 1).padStart(2, "0");
  const d = String(resolved.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
